import { firestore, functions } from './firebase';
import { addDocument, getDocument, getDocuments, getDocumentsAsArray, setDocument, updateDocument } from './firestore';

import { ContactStatus, ContactType, OrganizationType } from '@wonderschool/common-base-types';
import { initializeOrganizationSetup } from '../../helpers/organizations';
import { phoneNumberFormat } from '../../helpers/utils';
export const ORGANIZATION_VERSION = {
  v1: '1.0',
  v2: '2.0',
};
const DIRECTOR_PIN = '5499';

export type UserProfileType = {
  uid: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  phoneNumber: string;
  photoURL: string;
  defaultOrganization: string;
  organizations: { [key: string]: boolean };
  staffType: string[];
  allowStaffLogin: boolean;
  staff: boolean;
  staffPin: string;
  enabled: boolean;
  language: string;
  createdAt: any;
  currentRoom?: string;
  defaultRoom?: string;
  defaultLocation?: string;
  phone?: string;
  locations?: { [key: string]: boolean };
  rooms?: { [key: string]: boolean };
};

export const organizationOnListen = (organizationId: string, next: any, error: any, complete?: any) =>
  firestore()
    .collection(`organizations`)
    .doc(organizationId)
    .onSnapshot((doc) => next(doc ? { id: doc.id, ...doc.data() } : {}), error, complete);

export const provisionOrganizationUserAccount = async (data) => {
  try {
    if (!data) throw Error('user data is missing');
    if (!data.uid) throw Error('UID is required');

    const { uid, organizationName, programType, networkId, ...userData } = data;

    // CompleteSignupForm passes the signup phone as `phone`. It goes to the
    // fields the profile form reads and saves (user and contact `phone`, E.164
    // like the form writes), never to the legacy `phoneNumber`: contactToUser
    // maps contact `phoneNumber` onto user `phone` too, so a signup value there
    // would overwrite every later edit made through the profile.
    const phone = formatSignupPhone(userData.phone || userData.phoneNumber);

    // Create new organization.
    const organizationDocRef = firestore().collection('organizations').doc();
    const createdAt = firestore.FieldValue.serverTimestamp();

    const organizationData = {
      name: organizationName || `${userData.firstName} Program`,
      programType: programType || '',
      networks: networkId ? [networkId] : [],
      setup: initializeOrganizationSetup(),
      setupComplete: false,
      active: true,
      version: ORGANIZATION_VERSION.v2,
      createdBy: uid,
      createdAt,
      requireIdVerification: !networkId,
    };

    // Prepare user data to be saved.

    let userProfile: UserProfileType = {
      uid,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      displayName: userData.displayName || '',
      email: userData.email || '',
      phoneNumber: userData.phoneNumber || '',
      photoURL: userData.photoURL || '',
      defaultOrganization: organizationDocRef.id,
      organizations: { [organizationDocRef.id]: true },
      staffType: ['organizationAdmin'],
      allowStaffLogin: true,
      staff: true,
      staffPin: DIRECTOR_PIN,
      enabled: true,
      language: userData.language || '',
      ...(phone ? { phone } : {}),
      createdAt,
    };

    // Modify the payload if it's FCC.
    if (programType && programType === 'familyChildCare') {
      const locationId = `${organizationDocRef.id}-primaryLocation`;
      const roomId = `${organizationDocRef.id}-primaryRoom`;

      userProfile = {
        ...userProfile,
        currentRoom: roomId,
        defaultRoom: roomId,
        defaultLocation: locationId,
        locations: {
          [locationId]: true,
        },
        rooms: {
          [roomId]: true,
        },
      };
    }

    // Prepare contact data to be saved.
    const contact = {
      uid,
      type: ContactType.STAFF,
      status: ContactStatus.verified,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      displayName: userData.displayName || '',
      email: userData.email || '',
      phoneNumber: userData.phoneNumber || '',
      photoURL: userData.photoURL || '',
      defaultOrganization: organizationDocRef.id,
      organizations: [organizationDocRef.id],
      staffType: ['organizationAdmin'],
      allowLoginWithPin: true,
      loginPin: DIRECTOR_PIN,
      language: userData.language || '',
      currentRoom: userProfile.currentRoom || '',
      defaultRoom: userProfile.defaultRoom || '',
      defaultLocation: userProfile.defaultLocation || '',
      locations: Object.keys(userProfile.locations || {}),
      rooms: Object.keys(userProfile.rooms || {}),
      ...(phone ? { phone } : {}),
      createdAt,
    };
    const pinData = {
      active: true,
      recycled: false,
      organization: organizationDocRef.id,
      uid,
      type: 'staffPin',
      pin: DIRECTOR_PIN,
    };

    const batch = firestore().batch();

    const contactDocRef = firestore().collection(`organizations/${organizationDocRef.id}/contacts`).doc();
    const userDocRef = firestore().collection(`organizations/${organizationDocRef.id}/users`).doc(uid);
    const pinDocRef = firestore().collection(`organizations/${organizationDocRef.id}/tabletPins`).doc(DIRECTOR_PIN);

    batch.set(organizationDocRef, organizationData);
    batch.set(contactDocRef, contact);
    batch.set(userDocRef, {
      ...userProfile,
      contactId: contactDocRef.id,
    });
    batch.set(pinDocRef, pinData);

    await batch.commit();

    return userProfile;
  } catch (error: any) {
    console.error(error);
    throw Error(error?.message || 'Unable to complete provisioning');
  }
};

// E.164, as the profile form saves it. A number the formatter cannot parse is
// kept as typed rather than lost or allowed to fail provisioning.
const formatSignupPhone = (raw?: string): string => {
  if (!raw) return '';
  try {
    return phoneNumberFormat(raw);
  } catch {
    return raw;
  }
};

export const createOrganization = (organizationData) => {
  const data = {
    ...organizationData,
    setup: initializeOrganizationSetup(),
    setupComplete: false,
    active: true,
    shouldHideFromSearchResults: false,
    createdAt: Date.now(),
  };

  return addDocument({ path: 'organizations', data });
};

export const updateOrganization = (organizationId, organizationData) => {
  return updateDocument({
    path: `organizations/${organizationId}`,
    data: organizationData,
  });
};

export const getAllOrganizations = (limit = null, startAfter = null) =>
  getDocuments({
    path: 'organizations',
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
    limit,
    startAfter,
  });

export const getAllOrganizationsAsArray = (limit = null, startAfter = null) =>
  getDocumentsAsArray({
    path: 'organizations',
    orderBy: [{ field: 'createdAt', direction: 'asc' }],
    limit,
    startAfter,
  });

export async function getOrganizationById(organizationId: string): Promise<OrganizationType | null> {
  const org = await getDocument<OrganizationType>({
    path: `organizations/${organizationId}`,
  });
  return org;
}

export const getCreatePayoutAccountUrl = async (organizationId: string) => {
  const { data } = await functions().httpsCallable('callables-stripe-createPayoutAccount')({ organizationId });
  return data;
};

export type CreateAccountSessionResult = {
  clientSecret: string;
};

// Mints a Stripe Account Session for the org's connected account, scoped to the
// embedded Documents component, so the 1099-K page can initialize Stripe
// Connect.js. The client secret is short-lived; Connect.js calls this again to
// refresh it. The publishable key is a public client value read on the frontend
// from VITE_STRIPE_PUBLISHABLE_KEY, not returned here.
export const createAccountSession = async (organizationId: string): Promise<CreateAccountSessionResult> => {
  const { data } = await functions().httpsCallable('callables-stripe-createAccountSession')({ organizationId });
  return data as CreateAccountSessionResult;
};

export const getOrganizationSettings = async (organizationId: string) =>
  getDocuments({
    path: `organizations/${organizationId}/settings`,
  });

export const setOrganizationSettings = async (organizationId: string, settingsId: string, settings: any) =>
  setDocument({
    path: `organizations/${organizationId}/settings/${settingsId}`,
    data: settings,
  });
